import { useEffect, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import LavorisService from "../../../services/LavorisService";
import { Input } from "../../core/Input";
import { DatePicker } from "../DatePicker";
import InputCheckbox from "../InputCheckbox/InputCheckbox";
import InputRadio from "../InputRadio/InputRadio";
import InputRadioOp1 from "../InputRadioOp1/InputRadioOp1";
import InputRadioOp2 from "../InputRadioOp2/InputRadioOp2";
import SearchSelectFetch from "../SearchFetch";
import Select from "../Select/Select";
import Textarea from "../Textarea/Textarea";
import * as StyledWrapper from "./styles";
import { isNumericInput } from "./utils";
import { shouldRenderAlertInput } from "./utils";
import moment from "moment";

// New Form builder components migration
import { NumericInputBlock } from "./blocks/NumericInput";
import { PhoneNumber } from "../PhoneNumber";

const LOCKED_PERSONAL_FIELDS = ["text_1", "text_2", "text_3"];

type FormBuilderProps = {
  json: string;
  formMethods: UseFormReturn<any>;
  readOnly?: boolean;
  insideATable?: boolean;
  personalData?: any;
  lockPersonalFields?: boolean;
};

const FormBuilder = ({
  json,
  formMethods,
  readOnly,
  insideATable = false,
  personalData,
  lockPersonalFields = false,
}: FormBuilderProps) => {
  const LavorisInstance = LavorisService.getInstance();
  const [jsonParsed, setJsonParsed] = useState<any[]>([]);
  const [isUpdateDeclaration, setIsUpdateDeclaration] = useState(false);
  const {
    register,
    formState: { errors },
    control,
    getValues,
    setValue,
    watch,
    reset
  } = formMethods;
  const currentSex = watch("select_6") || ""

  useEffect(() => {
    try {
      setJsonParsed(JSON.parse(json) as any[]);
    } catch (error) { }
  }, [json]);

  const jsonToMap =
    jsonParsed && typeof jsonParsed === "object" && jsonParsed.length > 0
      ? jsonParsed
      : [];


  useEffect(() => {
    if (!personalData) return
    let sex = personalData[0]?.sex || ""
    if (sex) {
      sex = sex.toUpperCase();
    }
    let fechaNacimiento = personalData[0]?.birthDate    
    if (fechaNacimiento) {
      fechaNacimiento = moment(fechaNacimiento, "DD/MM/YYYY").format("ddd MMM DD YYYY HH:mm:ss [GMT]ZZZ [(Argentina Standard Time)]");
    }

    let maritalStatus = personalData[0]?.maritalStatus || "";
    if (maritalStatus) {
      maritalStatus = maritalStatus.toUpperCase();
      maritalStatus = (maritalStatus === "DIVORCIADA") ? "DIVORCIADO" : maritalStatus;
      maritalStatus = (maritalStatus === "SOLTERA") ? "SOLTERO" : maritalStatus;
      maritalStatus = (maritalStatus === "CASADA") ? "CASADO" : maritalStatus;
      maritalStatus = (maritalStatus === "VIUDA") ? "VIUDO" : maritalStatus;
    }
    let fullName = personalData[0]?.name.split(",");


    reset({
      text_1: fullName[1] ? fullName[0].trim() : "",
      text_2: fullName[1] ? fullName[1].trim() : fullName[0].trim(),
      text_3: personalData[0]?.DNI,
      calendar_5: fechaNacimiento,
      text_7: personalData[0]?.genre ? personalData[0]?.genre : personalData[0]?.gender,
      text_10: personalData[0]?.location ? personalData[0]?.location : personalData[0]?.city,
      text_11: personalData[0]?.fullAddress,
      phoneNumber_13: personalData[0]?.phoneNumber,
      select_4: personalData[0]?.nationality === "ARGENTINO" ? "ARGENTINA" :  personalData[0]?.nationality,
      select_6: sex,
      select_8: maritalStatus,

    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalData])

  const fetchDiagnoses = async () => {
    const { positions } = await LavorisInstance.PublicData.getPublicData();
    return positions.map((position) => ({
      id: position.value,
      label: position.name,
      value: position.value,
    }));
  };

  const renderNumericInputBlock = (
    obj: any,
    fieldName: string,
    error: any,
    customStyle: any,
  ) => (
    <NumericInputBlock
      defaultValue={0}
      label={obj.label}
      subType={obj.type}
      fieldName={fieldName}
      register={register}
      required={!!obj.required}
      readOnly={!!readOnly}
      error={error}
      min={obj.min}
      max={obj.max}
      getValues={getValues}
      setValue={setValue}
      style={customStyle}
    />
  );

  useEffect(() => {
    setIsUpdateDeclaration(window.location.pathname.includes("Declaracion-jurada/update"));
  }, []);

  return (
    <>
      {jsonToMap.map((obj, index) => {
        const error = errors?.[
          obj.fieldName || (`${obj.type}_${index}` as keyof typeof errors)
        ] as any;
        let fieldName = obj.fieldName || `${obj.type}_${index}`;
        if (insideATable)
          fieldName = `table_${obj.tableIndex}_${obj.columnIndex}_${fieldName}`;
        const customStyle = insideATable
          ? { height: "40px", width: "150px" }
          : {};
          const isRequired = obj.required && !isUpdateDeclaration;
          const isPersonalFieldLocked =
            lockPersonalFields && LOCKED_PERSONAL_FIELDS.includes(fieldName);
        return (
          <div key={index} className="mb-4">
            {isNumericInput(obj.type)
              ? renderNumericInputBlock(obj, fieldName, error, customStyle)
              : null}
            {obj.type === "text" ? (
              <Input
                type="text"
                label={obj.label || ""}
                fieldName={fieldName}
                register={register}
                readOnly={!!readOnly || isPersonalFieldLocked}
                required={!!isRequired}
                error={error}
                style={customStyle}
              />
            ) : null}
            {obj.type === "calendar" ? (
              <DatePicker
                fieldName={fieldName}
                label={obj.label}
                minDate={obj.minDate}
                maxDate={obj.maxDate}
                readOnly={!!readOnly}
                control={control}
                required={!!isRequired}
                error={error}
                style={customStyle}
              />
            ) : null}
            {obj.type === "select" ? (
              <Select
                fieldName={fieldName}
                label={obj.label}
                register={register}
                required={!!isRequired}
                readOnly={!!readOnly}
                error={error}
                options={obj.options ? obj.options : [{ name: "", value: "" }]}
                styles={`rounded w-52 md:w-auto h-[${insideATable ? customStyle.height : "50px"}] `}
              />
            ) : null}
            {
              obj.type === "phoneNumber" ? (
                <PhoneNumber
                  fieldName={fieldName}
                  label={obj.label}
                  error={error}
                  required={!!isRequired}
                  control={control}
                />
              ) : null
            }
            {obj.type === "textarea" ? (
              <Textarea
                fieldName={fieldName}
                label={obj.label || ""}
                register={register}
                required={!!isRequired}
                readOnly={!!readOnly}
                error={error}
                styles="rounded w-full"
                rows={obj.rows}
              />
            ) : null}
            {obj.type === "radio" ? (
              <InputRadio
                fieldName={fieldName}
                label={obj.label}
                register={register}
                required={!!isRequired}
                error={error}
                readOnly={!!readOnly}
                option1={obj.option1}
                option2={obj.option2}
                getValues={getValues}
                globalContainerStyles={"flex-col md:flex-row justify-between"}
                labelStyles={"mb-3 md:mb-0"}
              />
            ) : null}
            {obj.type === "radio1" ? (
              <InputRadioOp1
                fieldName={fieldName}
                label={obj.label}
                register={register}
                required={!!isRequired}
                error={error}
                readOnly={!!readOnly}
                option1={obj.option1}
                option2={obj.option2}
                labelForComment={obj.labelForComment}
                getValues={getValues}
                setValue={setValue}
              />
            ) : null}
            {obj.type === "radio2" ? (
              <InputRadioOp2
                fieldName={fieldName}
                label={obj.label}
                register={register}
                required={!!isRequired}
                error={error}
                readOnly={!!readOnly}
                option1={obj.option1}
                option2={obj.option2}
                labelForComment={obj.labelForComment}
                getValues={getValues}
                setValue={setValue}

              />
            ) : null}
            {obj.type === "checkbox" ? (
              <div>
                <p className="mb-4">{obj.label}</p>
                {Array.isArray(obj.options) && (
                  <div className="flex flex-col">
                    {obj.options?.map(
                      (option: { name: string; value: string }, i: number) => (
                        <div className={"mb-2 flex"}>
                          <InputCheckbox
                            readOnly={!!readOnly}
                            fieldName={`${fieldName}_option_${i}`}
                            label={option.name?.toString()}
                            register={register}
                            error={error}
                          />
                        </div>
                      ),
                    )}
                  </div>
                )}
              </div>
            ) : null}
            {obj.type === "singleBox" && (
              <InputCheckbox
                readOnly={!!readOnly}
                fieldName={`${fieldName}_option_${index}`}
                label={obj.label}
                register={register}
                error={error}
              />
            )}
            {obj.type === "table" && !insideATable && (
              <div>
                <p className="mb-4">{obj.label}</p>
                {Array.isArray(obj.columns) && (
                  <div className={`grid grid-cols-${obj.columns.length} gap-2`}>
                    {[...obj.columns]
                      .sort((a: any, b: any) => a?.order - b?.order)
                      .map((column: any, columnIndex) => (
                        <div className="flex flex-col">
                          <FormBuilder
                            json={JSON.stringify(
                              column.cells.map((cell: any) => ({
                                ...cell,
                                tableIndex: index,
                                columnIndex,
                              })),
                            )}
                            formMethods={formMethods}
                            insideATable={true}
                          />
                        </div>
                      ))}
                  </div>
                )}
              </div>
            )}
            {obj.type === "diagnosticos" && (
              <SearchSelectFetch
                control={control}
                name={fieldName}
                label={obj.label}
                required={!!isRequired}
                fetchOptions={fetchDiagnoses}
                propertyLabel={"label"}
                inputStyles={`grow rounded bg-transparent text-sm !mb-6 h-[${insideATable ? customStyle.height : "50px"
                  }]`}
              />
            )}
            {obj.type === "title" ? (
              <StyledWrapper.Tittle>{obj.label}</StyledWrapper.Tittle>
            ) : null}
            {obj.type === "subtitle" ? (
              <StyledWrapper.SubTittle>{obj.label}</StyledWrapper.SubTittle>
            ) : null}
            {obj.type === "normalText" ? <p>{obj.label}</p> : null}

            {/* HAS ALERT INPUT */}
            {shouldRenderAlertInput(obj) && (
              <div className="mt-2">
                <InputRadioOp1
                  key={`${index}_alert`}
                  fieldName={`${fieldName}_alert`}
                  label={"Alerta"}
                  register={register}
                  error={error}
                  option1={"Si"}
                  option2={"No"}
                  readOnly={readOnly}
                  rows={5}
                  getValues={getValues}
                  setValue={setValue}
                  labelForComment={""}
                />
              </div>
            )}

            {
              currentSex === "FEMENINO" && obj.type === "radio1Optional" && (
                <InputRadioOp1
                  fieldName={obj.fieldName}
                  label={obj.label}
                  register={register}
                  required={!!isRequired}
                  error={error}
                  readOnly={!!readOnly}
                  option1={obj.option1}
                  option2={obj.option2}
                  labelForComment={obj.labelForComment}
                  getValues={getValues}
                  setValue={setValue}
                />
              )
            }

            {
              currentSex === "FEMENINO" && obj.type === "calendarOptional" && (
                <div className="flex flex-col justify-between align-middle">
                  <div className="m-2 w-fit">
                    <DatePicker
                      fieldName={obj.fieldName}
                      label={obj.label}
                      minDate={obj.minDate}
                      maxDate={obj.maxDate}
                      readOnly={!!readOnly}
                      control={control}
                      required={!!isRequired}
                      error={error}
                      style={customStyle}
                    />
                  </div>
                </div>)
            }

          </div>
        );
      })}
    </>
  );
};

export default FormBuilder;
